import { useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";

import { makeStyles, Typography, Grid } from "@material-ui/core";

import { BY_MY } from "../../../../constants";
import { api } from "../../../../services/api";
import { Toggle } from "../../../../components/toggle";
import { requestDateFormat } from "../../../../services/utils";
import { ShaDowBox } from "../../../../components/boxes/ShadowBox";
import { POWER_ASSETS_COLORS, POWER_ASSETS_LEGENDS } from "../../constants";
import { GRAPH_UNITS } from "../../../dashboard/constants";
import { StackedAreaChart } from "../../../../components/charts/StackedAreaChart";
import { NoDataCard } from "../../../../components/parts";

const useStyles = makeStyles((theme) => ({
  box: {
    padding: `${theme.spacing(3)}px ${theme.spacing(3.5)}px`,
  },
  areaChart: {
    marginTop: `${theme.spacing(5.25)}px`,
    height: "240px",
  },
}));

export const ProductionOverTime = ({ endpoint, period, startDate, endDate, graphUnit }) => {
  const classes = useStyles();
  const [data, setData] = useState([]);
  const [byFilter, setByFilter] = useState();
  const [defaultFilter, setDefaultFilter] = useState();
  const [currentUrl, setCurrentUrl] = useState();
  const [rawData, setRawData] = useState([]);
  const currencySymbol = useSelector((state) => state.auth.user.currency.symbol);
  const { cluster, segments, selectedCountries } = useSelector((state) => state.selectCSC);

  const title = "Production - Over Time";

  useEffect(() => {
    if (rawData) {
      const data = rawData.map((item) => {
        const date = new Date(item.date);
        return {
          ...item,
          solor: graphUnit === GRAPH_UNITS.kwh ? item.solar_kwh : item.solar,
          battery: graphUnit === GRAPH_UNITS.kwh ? item.battery_kwh : item.battery,
          ev: graphUnit === GRAPH_UNITS.kwh ? item.ev_kwh : item.ev,
          unit: graphUnit === GRAPH_UNITS.kwh ? "kWh" : currencySymbol,
          x: `${date.getUTCFullYear()}-${date.getUTCMonth() + 1}`,
        };
      });
      setData(data);
    }
  }, [rawData, graphUnit]);

  useEffect(() => {
    const getData = (url) => {
      api.get(url).then((resp) => {
        setRawData(resp.data.results);
        setDefaultFilter(resp.data.date_format === "month" ? BY_MY[0] : BY_MY[1]);
      });
    };

    setByFilter(null);
    let url = endpoint ? endpoint : "/praas/v1/power-assets/production-time/";
    if (period.name === "RANGE" && endDate) {
      url += `?from_date=${requestDateFormat(startDate)}&to_date=${requestDateFormat(endDate)}`;
      getData(url);
      setCurrentUrl(url);
    } else if (["YTD", "Acc.", "20Y"].includes(period.name)) {
      url += `?date_filter=${period.filter}`;
      getData(url);
      setCurrentUrl(url);
    }
  }, [period, startDate, endDate, cluster, segments, selectedCountries]);

  useEffect(() => {
    if (byFilter) {
      let dateFormat = `date_format=${byFilter === BY_MY[0] ? "month" : "year"}`;
      api.get(`${currentUrl}&${dateFormat}`).then((resp) => {
        const data = resp.data.results.map((item) => {
          const date = new Date(item.date);
          return {
            ...item,
            x: `${date.getUTCFullYear()}-${date.getUTCMonth() + 1}`,
          };
        });
        setData(data);
      });
    }
  }, [currentUrl, byFilter]);

  const areas = useMemo(() => {
    return [
      {
        key: graphUnit === GRAPH_UNITS.kwh ? "solar_kwh" : "solar",
        stroke: POWER_ASSETS_COLORS[POWER_ASSETS_LEGENDS.SOLAR],
        fill: POWER_ASSETS_COLORS[POWER_ASSETS_LEGENDS.SOLAR],
      },
      {
        key: graphUnit === GRAPH_UNITS.kwh ? "battery_kwh" : "battery",
        stroke: POWER_ASSETS_COLORS[POWER_ASSETS_LEGENDS.BATTERY],
        fill: POWER_ASSETS_COLORS[POWER_ASSETS_LEGENDS.BATTERY],
      },
      {
        key: graphUnit === GRAPH_UNITS.kwh ? "ev_kwh" : "ev",
        stroke: POWER_ASSETS_COLORS[POWER_ASSETS_LEGENDS.EV],
        fill: POWER_ASSETS_COLORS[POWER_ASSETS_LEGENDS.EV],
      },
    ];
  }, [graphUnit]);

  return (
    <ShaDowBox fullHeight>
      {data.length ? (
        <div className={classes.box} id="production-overtime">
          <Grid container justifyContent="space-between" alignItems="center">
            <Grid item>
              <Typography variant="h4" style={{ marginRight: 3 }}>
                {title}
              </Typography>
            </Grid>
            <Grid
              item
              container
              justifyContent="flex-end"
              alignItems="center"
              style={{ flex: 1, flexWrap: "nowrap" }}
            >
              <Grid container item style={{ width: "fit-content", flexWrap: "nowrap" }}>
                <Typography style={{ marginRight: 4 }}>By:</Typography>
                <Toggle
                  value={byFilter || defaultFilter}
                  options={BY_MY}
                  onChanged={setByFilter}
                  styles={{ width: 14 }}
                />
              </Grid>
            </Grid>
          </Grid>
          <div className={classes.areaChart}>
            <StackedAreaChart
              data={data}
              areas={areas}
              units={graphUnit === GRAPH_UNITS.kwh ? "kwh" : currencySymbol}
              onlyLeftRightXAxis
              hideYAxis
              hideXAxis
              hideSumOnToolTip
            />
          </div>
        </div>
      ) : (
        <NoDataCard title={title} />
      )}
    </ShaDowBox>
  );
};
